import React, { useMemo, useState } from 'react';
import { Grid2, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetBookings } from '@openapi/bookings/bookings';
import PageWithTitle from '../../components/atom/PageWithTitle';
import SearchInput from '../../components/atom/SearchInput';
import useNotify from '../../hooks/useNotify';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import CustomersTable from './components/CustomersTable';
import { groupBookingsByCustomer, filterCustomers } from './helpers/groupCustomers';

// Cap the bookings we pull in to keep grouping cheap. The CRM is a frontend lens
// over the bookings list — narrowing happens via the search field.
const CRM_BOOKINGS_PAGE_SIZE = 1000;

const CRMPage = () => {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [search, setSearch] = useState('');
    const [params, setParams] = useState({
        page: 0,
        pageSize: TABLE_ROW_COUNT_DEFAULT,
    });

    const { data, isLoading } = useGetBookings(
        {
            page: 0,
            pageSize: CRM_BOOKINGS_PAGE_SIZE,
            sort: 'created_at',
            sortDir: 'DESC',
            search: search || undefined,
            includeSummary: false,
        },
        { query: { onError: notifyError } },
    );

    const customers = useMemo(
        () => groupBookingsByCustomer(data?.bookings || []),
        [data?.bookings],
    );

    // Search is already applied server-side; keeping the client filter in place
    // ensures the visible customer list responds to the same needle even before
    // the next request settles.
    const filteredCustomers = useMemo(
        () => filterCustomers(customers, search),
        [customers, search],
    );

    return (
        <PageWithTitle
            title={t('crm.title')}
            stackHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                    alignItems="center"
                    key="crm-search"
                    sx={{ width: '100%' }}
                >
                    <Grid2 size={{ xs: 12, md: 'auto' }}>
                        <SearchInput
                            search={search}
                            setSearch={setSearch}
                            setParams={setParams}
                            limit={params.pageSize}
                            placeholder={t('crm.searchPlaceholder')}
                            fullWidth={isMobile}
                        />
                    </Grid2>
                </Grid2>,
            ]}
        >
            <Grid2 size={{ xs: 12 }}>
                <CustomersTable
                    customers={filteredCustomers}
                    loading={isLoading}
                    page={params.page}
                    limit={params.pageSize}
                    setParams={setParams}
                    total={filteredCustomers.length}
                />
            </Grid2>
        </PageWithTitle>
    );
};

export default CRMPage;
